import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
  RowSelectionOptions,
  themeQuartz,
} from "ag-grid-community";
import { useCallback, useMemo } from "react";
import { VehicleType, WorkOrderAssignmentStatus } from "@/gql/graphql";
import { Link } from "@tanstack/react-router";
import dayjs from "dayjs";
import { ReportAssignmentListItem } from "./GraphQL/reportWorkOrderAssignmentsQuery";
import { workOrderMessages } from "@/app/ProDrivers/WorkOrders/messages/workOrderMessages";
import { assignmentStatusColorMap } from "@/app/ProDrivers/WorkOrders/utils/assignmentStatusColorMap";
import { Badge } from "@/components/catalyst/badge";
import { vehicleMessages } from "@/app/Vehicles/messages/vehicleMessages";

const imoovaTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 12,
  headerFontSize: 14,
  spacing: 5,
});

const defaultColDef = {
  floatingFilter: true,
};

const sidebar = {
  toolPanels: ["columns", "filters"],
};

interface ReportAssignmentsTableProps {
  ref: any;
  data: ReportAssignmentListItem[];
  isLoading: boolean;
}

function isSummaryRow(data: unknown): boolean {
  return (data as { _isSummaryRow?: boolean })?._isSummaryRow === true;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  return dayjs(value).format("DD/MM/YYYY");
}

const lateCellStyle = {
  backgroundColor: "#fef2f2",
  color: "#b91c1c",
  fontWeight: 600,
};

const lateValueStyle = {
  color: "#dc2626",
  fontWeight: 700,
};

export function ReportAssignmentsTable({
  ref,
  data,
  isLoading,
}: ReportAssignmentsTableProps) {
  const columns = useMemo(() => {
    return [
      {
        field: "work_order_reference",
        headerName: "Work Order #",
        filter: "agTextColumnFilter",
        pinned: "left",
        cellRenderer: (
          params: ICellRendererParams<ReportAssignmentListItem>,
        ) => {
          if (!params.data?.work_order_id || isSummaryRow(params.data)) {
            return params.value;
          }
          const id = params.data.work_order_id;
          return (
            <Link
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
              params={(prev) => ({ ...prev, id })}
              className="text-blue-600 hover:underline"
            >
              {params.value}
            </Link>
          );
        },
      },
      {
        field: "departure_city",
        headerName: "Route",
        filter: "agTextColumnFilter",
        valueGetter: (params) => {
          if (!params.data?.departure_city && !params.data?.delivery_city) {
            return null;
          }
          const from = params.data?.departure_city ?? "—";
          const to = params.data?.delivery_city ?? "—";
          return `${from} → ${to}`;
        },
      },
      {
        field: "supplier_name",
        headerName: "Supplier",
        filter: "agTextColumnFilter",
      },
      {
        field: "assignment_status",
        headerName: "Status",
        filter: "agSetColumnFilter",
        filterParams: {
          values: Object.values(WorkOrderAssignmentStatus),
          valueFormatter: (params: { value: WorkOrderAssignmentStatus }) =>
            workOrderMessages.assignment.status[params.value] ?? params.value,
        },
        enableRowGroup: true,
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value === undefined || isSummaryRow(params.data)) {
            return null;
          }

          const status = params.value as WorkOrderAssignmentStatus;
          return (
            <Badge color={assignmentStatusColorMap[status] ?? "zinc"}>
              {workOrderMessages.assignment.status[status] ?? status}
            </Badge>
          );
        },
      },
      {
        field: "pickup_date",
        headerName: "Pickup",
        valueGetter: (params) => {
          if (!params.data?.pickup_date) return null;
          return dayjs(params.data.pickup_date).toDate();
        },
        valueFormatter: (params) => formatDate(params.value),
        filter: "agDateColumnFilter",
        cellStyle: (params) =>
          params.data?.pickup_hours_late != null ? lateCellStyle : null,
      },
      {
        field: "pickup_hours_late",
        headerName: "Pickup Hrs Late",
        filter: "agNumberColumnFilter",
        cellStyle: (params) =>
          params.value != null && !isSummaryRow(params.data)
            ? lateValueStyle
            : null,
      },
      {
        field: "delivery_date",
        headerName: "Delivery",
        valueGetter: (params) => {
          if (!params.data?.delivery_date) return null;
          return dayjs(params.data.delivery_date).toDate();
        },
        valueFormatter: (params) => formatDate(params.value),
        filter: "agDateColumnFilter",
        cellStyle: (params) =>
          params.data?.delivery_hours_late != null ? lateCellStyle : null,
      },
      {
        field: "delivery_hours_late",
        headerName: "Delivery Hrs Late",
        filter: "agNumberColumnFilter",
        cellStyle: (params) =>
          params.value != null && !isSummaryRow(params.data)
            ? lateValueStyle
            : null,
        cellRenderer: (
          params: ICellRendererParams<ReportAssignmentListItem>,
        ) => {
          if (isSummaryRow(params.data)) {
            return (params.data as any)?._lateCount != null
              ? `Late: ${(params.data as any)._lateCount}`
              : null;
          }
          return params.value ?? null;
        },
      },
      {
        field: "driver_name",
        headerName: "Driver",
        filter: "agTextColumnFilter",
      },
      {
        field: "driver_email",
        headerName: "Driver Email",
        filter: "agTextColumnFilter",
      },
      {
        field: "driver_phone",
        headerName: "Driver Phone",
        filter: "agTextColumnFilter",
      },
      {
        field: "driver_age",
        headerName: "Driver Age",
        filter: "agNumberColumnFilter",
      },
      {
        field: "vehicle_type",
        headerName: "Vehicle",
        filter: "agSetColumnFilter",
        filterParams: {
          values: Object.values(VehicleType),
          valueFormatter: (params: { value: VehicleType }) =>
            vehicleMessages.type[params.value] ?? params.value,
        },
        enableRowGroup: true,
        valueFormatter: (params) => {
          if (!params.value) return "";
          return (
            vehicleMessages.type[params.value as VehicleType] ?? params.value
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created",
        valueGetter: (params) => {
          if (!params.data?.created_at) return null;
          return dayjs.utc(params.data.created_at).toDate();
        },
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs(params.value).format("DD/MM/YYYY");
        },
        filter: "agDateColumnFilter",
      },
    ] as ColDef<ReportAssignmentListItem>[];
  }, []);

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return {
      mode: "multiRow",
    };
  }, []);

  const pinnedBottomRowData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    const lateCount = data.filter(
      (row) => row.delivery_hours_late != null,
    ).length;

    return [
      {
        work_order_reference: `Total Assignments: ${data.length}`,
        _isSummaryRow: true,
        _lateCount: lateCount,
      },
    ];
  }, [data]);

  const getRowStyle = useCallback((params: any) => {
    if (params.data?._isSummaryRow) {
      return {
        backgroundColor: "#f3f4f6",
      };
    }
    return undefined;
  }, []);

  return (
    <AgGridReact<ReportAssignmentListItem>
      className="grow"
      ref={ref}
      loading={isLoading}
      rowData={data}
      rowSelection={rowSelection}
      theme={imoovaTheme}
      columnDefs={columns}
      defaultColDef={defaultColDef}
      enableCharts={true}
      cellSelection={true}
      sideBar={sidebar}
      pinnedBottomRowData={pinnedBottomRowData}
      getRowStyle={getRowStyle}
    />
  );
}
